import type { AnalyticsEventName, AnalyticsEventProperties } from './events';

/** Subconjunto das opções de `capture` do SDK que o produto usa. */
export interface CaptureDeliveryOptions {
  send_instantly: true;
  transport: 'sendBeacon';
}

export interface AnalyticsClient {
  capture(
    name: AnalyticsEventName,
    properties: AnalyticsEventProperties[AnalyticsEventName],
    options?: CaptureDeliveryOptions,
  ): void;
}

export interface TrackOptions {
  /**
   * O evento é emitido imediatamente antes de a página sair (ex.: redirect ao Stripe na etapa 6).
   * O lote normal do SDK (~3 s) morre com a página — medido no E2E da T1.18: o redirect sai 15 ms
   * depois. Pede envio imediato por sendBeacon, o único transporte que o browser garante depois do
   * unload. Não usar por conveniência: fura o agrupamento que poupa pedidos.
   */
  beforeNavigation?: boolean;
}

const BEFORE_NAVIGATION: CaptureDeliveryOptions = { send_instantly: true, transport: 'sendBeacon' };

export type TrackResult =
  | { status: 'blocked' }
  | { status: 'captured' }
  | { status: 'failed' }
  | { status: 'queued'; droppedOldest: boolean };

type QueuedEvent = {
  [Name in AnalyticsEventName]: {
    name: Name;
    properties: AnalyticsEventProperties[Name];
    options?: TrackOptions;
  };
}[AnalyticsEventName];

export interface AnalyticsTracker {
  track<Name extends AnalyticsEventName>(
    name: Name,
    properties: AnalyticsEventProperties[Name],
    options?: TrackOptions,
  ): TrackResult;
  setClient(client: AnalyticsClient | null): number;
  clearPending(): number;
}

export interface CreateAnalyticsTrackerInput {
  hasStatisticsConsent: () => boolean;
  maxQueueSize?: number;
}

/**
 * Cria a porta única de captura. O consentimento é relido em cada operação porque pode ser revogado
 * entre o call site, o carregamento assíncrono do SDK e o flush da fila.
 */
export function createAnalyticsTracker({
  hasStatisticsConsent,
  maxQueueSize = 50,
}: CreateAnalyticsTrackerInput): AnalyticsTracker {
  const queueLimit = Math.max(1, Math.floor(maxQueueSize));
  const queue: QueuedEvent[] = [];
  let activeClient: AnalyticsClient | null = null;

  const clearPending = (): number => {
    const cleared = queue.length;
    queue.length = 0;
    return cleared;
  };

  const capture = (queuedEvent: QueuedEvent): boolean => {
    if (!activeClient) return false;

    try {
      if (queuedEvent.options?.beforeNavigation) {
        activeClient.capture(queuedEvent.name, queuedEvent.properties, BEFORE_NAVIGATION);
      } else {
        activeClient.capture(queuedEvent.name, queuedEvent.properties);
      }
      return true;
    } catch {
      return false;
    }
  };

  return {
    track<Name extends AnalyticsEventName>(
      name: Name,
      properties: AnalyticsEventProperties[Name],
      options?: TrackOptions,
    ): TrackResult {
      if (!hasStatisticsConsent()) return { status: 'blocked' };

      const queuedEvent = (options ? { name, properties, options } : { name, properties }) as QueuedEvent;

      if (activeClient) {
        return capture(queuedEvent) ? { status: 'captured' } : { status: 'failed' };
      }

      const droppedOldest = queue.length >= queueLimit;
      if (droppedOldest) queue.shift();
      queue.push(queuedEvent);
      return { status: 'queued', droppedOldest };
    },

    setClient(client: AnalyticsClient | null): number {
      activeClient = client;
      if (!hasStatisticsConsent()) {
        clearPending();
        return 0;
      }

      let captured = 0;
      for (const queuedEvent of queue.splice(0)) {
        if (capture(queuedEvent)) captured += 1;
      }
      return captured;
    },

    clearPending,
  };
}
